(function () {
  "use strict";

  class MultiStepPopup {
    constructor(element) {
      this.element = element;
      this.dialog = element.querySelector(".multi-step-popup__dialog");
      this.options = this.parseOptions();
      this.currentStep = "intro";
      this.lastFocusedElement = null;
      this.openTimer = null;
      this.retryTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onSubscribed = this.onSubscribed.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.element.addEventListener("click", this.onClick);
      this.element.addEventListener("newsletter:subscribed", this.onSubscribed);
      document.addEventListener("keydown", this.onKeyDown);
      document.addEventListener("shopify:block:select", this.onBlockSelect);
      document.addEventListener("shopify:block:deselect", this.onBlockDeselect);

      this.initializePhoneInput();

      if (!window.Shopify || !Shopify.designMode) {
        this.scheduleOpen();
      }
    }

    parseOptions() {
      try {
        return JSON.parse(this.element.getAttribute("data-popup-settings") || "{}");
      } catch (error) {
        return {};
      }
    }

    initializePhoneInput(attempt = 0) {
      const phoneInput = this.element.querySelector("[data-intl-phone]");
      if (!phoneInput || phoneInput.intlTelInputInstance) return;

      if (typeof window.intlTelInput !== "function") {
        if (attempt < 50) {
          window.setTimeout(() => this.initializePhoneInput(attempt + 1), 100);
        }
        return;
      }

      phoneInput.intlTelInputInstance = window.intlTelInput(phoneInput, {
        initialCountry: "ca",
        allowDropdown: false,
        nationalMode: true,
        utilsScript:
          "https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js",
      });
    }

    scheduleOpen() {
      if (this.options.showOnlyOnce && this.wasPreviouslyShown()) return;

      const delay = Math.max(0, Number(this.options.apparitionDelay) || 0) * 1000;
      this.openTimer = window.setTimeout(() => this.openWhenAvailable(), delay);
    }

    openWhenAvailable() {
      const otherPopup = document.querySelector(
        '[data-popup-type]:not([data-popup-type="multi-step"])[aria-hidden="false"]',
      );

      if (otherPopup) {
        this.retryTimer = window.setTimeout(() => this.openWhenAvailable(), 3000);
        return;
      }

      this.open();
    }

    wasPreviouslyShown() {
      try {
        return localStorage.getItem(this.options.storageKey) === "true";
      } catch (error) {
        return false;
      }
    }

    rememberShown() {
      if (!this.options.showOnlyOnce) return;

      try {
        localStorage.setItem(this.options.storageKey, "true");
      } catch (error) {
        // The popup still works when storage is unavailable.
      }
    }

    open() {
      if (this.element.getAttribute("aria-hidden") === "false") return;

      this.lastFocusedElement = document.activeElement;
      this.showStep("intro", false);
      this.element.setAttribute("aria-hidden", "false");
      document.body.classList.add("multi-step-popup-open");
      this.rememberShown();

      window.requestAnimationFrame(() => {
        const firstAction = this.element.querySelector("[data-multi-step-next]");
        if (firstAction) firstAction.focus({ preventScroll: true });
      });
    }

    close() {
      this.element.setAttribute("aria-hidden", "true");
      document.body.classList.remove("multi-step-popup-open");

      if (
        this.lastFocusedElement &&
        typeof this.lastFocusedElement.focus === "function"
      ) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    showStep(name, moveFocus = true) {
      this.currentStep = name;

      this.element.querySelectorAll("[data-multi-step]").forEach((step) => {
        step.hidden = step.getAttribute("data-multi-step") !== name;
      });

      if (!moveFocus) return;

      window.requestAnimationFrame(() => {
        const target =
          name === "form"
            ? this.element.querySelector('input[type="email"]')
            : this.element.querySelector(
                `[data-multi-step="${name}"] .multi-step-popup__button`,
              );
        if (target) target.focus({ preventScroll: true });
      });
    }

    onClick(event) {
      if (event.target.closest("[data-multi-step-close]")) {
        this.close();
        return;
      }

      if (event.target.closest("[data-multi-step-next]")) {
        this.showStep("form");
        return;
      }

      if (event.target.closest("[data-multi-step-back]")) {
        this.showStep("intro");
        return;
      }

      if (event.target === this.element) this.close();
    }

    onKeyDown(event) {
      if (this.element.getAttribute("aria-hidden") !== "false") return;

      if (event.key === "Escape") {
        this.close();
        return;
      }

      if (event.key !== "Tab" || !this.dialog) return;

      const focusable = Array.from(
        this.dialog.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((node) => !node.closest("[hidden]"));

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onSubscribed(event) {
      if (!this.element.contains(event.target)) return;
      this.showStep("success");
    }

    onBlockSelect(event) {
      if (event.target !== this.element) return;
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.retryTimer);
      this.open();
    }

    onBlockDeselect(event) {
      if (event.target === this.element) this.close();
    }
  }

  function initialize(root) {
    root.querySelectorAll('[data-popup-type="multi-step"]').forEach((element) => {
      if (element.multiStepPopup) return;
      element.multiStepPopup = new MultiStepPopup(element);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(document));
  } else {
    initialize(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initialize(event.target);
  });
})();
